"use client";

import { createContext, useContext, useEffect, useState, useCallback } from "react";

// Define the shape of a cart item
export interface CartItem {
  id: string;
  title: string;
  price: string;
  qty: number;
  totalPrice: number;
  email?: string; // Optional email for workshop bookings
  workshop?: string; // Optional workshop name for workshop bookings
}

// Define the type for customer details
export type CustomerDetails = {
  name: string;
  address1: string;
  address2: string;
  city: string;
  county: string;
  postcode: string;
  phone: string;
  email: string;
};

// Extend the shape of the CartContext
interface CartContextType {
  cart: CartItem[];
  addToCart: (product: CartItem) => void;
  removeFromCart: (productId: string) => void;
  clearCart: () => void;
  checkoutId?: string;
  checkoutReference?: string;
  setCheckoutInfo: (id: string, reference: string) => void;
  customerDetails?: CustomerDetails;
  setCustomerDetails: (details: CustomerDetails) => void;
}

// Create Context with the correct type
const CartContext = createContext<CartContextType | null>(null);

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [checkoutId, setCheckoutId] = useState<string | undefined>(undefined);
  const [checkoutReference, setCheckoutReference] = useState<string | undefined>(undefined);
  const [customerDetails, setCustomerDetailsState] = useState<CustomerDetails | undefined>(undefined);

  // ✅ Load Cart, Checkout Info, and Customer Details from Local Storage on Initial Render
  useEffect(() => {
    try {
      const storedCart = localStorage.getItem("cart");
      const storedCheckoutId = localStorage.getItem("checkoutId");
      const storedCheckoutRef = localStorage.getItem("checkoutReference");
      const storedCustomerDetails = localStorage.getItem("customerDetails");

      if (storedCart) setCart(JSON.parse(storedCart));
      if (storedCheckoutId) setCheckoutId(storedCheckoutId);
      if (storedCheckoutRef) setCheckoutReference(storedCheckoutRef);
      if (storedCustomerDetails) setCustomerDetailsState(JSON.parse(storedCustomerDetails));
    } catch (error) {
      console.error("Error parsing data from storage:", error);
      setCart([]);
    }
  }, []);

  // ✅ Save Cart to Local Storage When It Updates
  useEffect(() => {
    if (cart.length > 0) {
      localStorage.setItem("cart", JSON.stringify(cart));
    } else {
      localStorage.removeItem("cart");
    }
  }, [cart]);

  // ✅ Save Checkout Info to Local Storage When It Updates
  useEffect(() => {
    if (checkoutId) localStorage.setItem("checkoutId", checkoutId);
    if (checkoutReference) localStorage.setItem("checkoutReference", checkoutReference);
  }, [checkoutId, checkoutReference]);

  // ✅ Save Customer Details to Local Storage When It Updates
  useEffect(() => {
    if (customerDetails) {
      localStorage.setItem("customerDetails", JSON.stringify(customerDetails));
    } else {
      localStorage.removeItem("customerDetails");
    }
  }, [customerDetails]);

  const addToCart = (product: CartItem) => {
    setCart((prevCart) => {
      const existingItemIndex = prevCart.findIndex((item) => item.id === product.id);
  
      if (existingItemIndex !== -1) {
        // Update the quantity of the existing item
        const updatedCart = [...prevCart];
        updatedCart[existingItemIndex].qty += product.qty;
        return updatedCart;
      }
  
      // Add the new product to the cart
      return [...prevCart, { ...product }];
    });
  };

  const removeFromCart = (productId: string) => {
    setCart((prevCart) => prevCart.filter((item) => item.id !== productId));
  };

  const clearCart = useCallback(() => {
    setCart([]);
    setCheckoutId(undefined);
    setCheckoutReference(undefined);
    setCustomerDetailsState(undefined);
    localStorage.removeItem("cart");
    localStorage.removeItem("checkoutId");
    localStorage.removeItem("checkoutReference");
    localStorage.removeItem("customerDetails");
  }, []);

  const setCheckoutInfo = (id: string, reference: string) => {
    setCheckoutId(id);
    setCheckoutReference(reference);
  };

  const setCustomerDetails = (details: CustomerDetails) => {
    setCustomerDetailsState(details);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        clearCart,
        checkoutId,
        checkoutReference,
        setCheckoutInfo,
        customerDetails,
        setCustomerDetails,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

// Hook to Access Cart Anywhere in the App
export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

export { CartContext };